import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Coins,
  Gift,
  Layers,
  Map as MapIcon,
  MapPin,
  TrendingUp,
  Trophy,
  UserCheck,
  Wallet,
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useMetricasCanjes,
  useMetricasPorCampo,
  useMetricasPorProvincia,
  useResumenFidelizacion,
  useSerieTemporal,
  useTodosParticipantesFidelizacion,
  useWalletBalance,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { normalizeColor } from '../lib/reporteColor'
import { monedaPais, formatMoneda, formatNumero, regionTerminoCap, esPeru } from '../lib/paisMapa'
import { nombreDistritoReporte } from '../lib/distritosMedifarmaPeru'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { ReportePeriodoBar } from '../components/ReportePeriodoBar'
import { StatCardConsolidado } from '../components/StatCardConsolidado'
import { ListadoTab } from '../components/ListadoTab'
import { BarChartAgrupado, type DatasetOpcion } from '../components/charts/BarChartAgrupado'
import { AreaChartFecha } from '../components/charts/AreaChartFecha'
import { parseNumeric } from '../components/charts/palette'
import { DynamicFiltersBar, type DynamicFiltersValue, type FieldOption } from '../components/DynamicFiltersBar'
import { autoFieldOptions, participantePairs } from '../lib/campos'
import { useCascadaRepresentante } from '../hooks/useCascadaRepresentante'

const CAMPO_CADENA_CANDIDATES = ['cadena', 'farmacia', 'codigo_farmacia']
const CAMPO_REPRESENTANTE_CANDIDATES = ['representante', 'rep', 'gerente_zona']
const CAMPO_DISTRITO_CANDIDATES = ['distrito', 'ciudad']

function pickCampo(
  campos: Array<{ key: string; agrupable: boolean; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.agrupable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

function pickCampoFiltrable(
  campos: Array<{ key: string; filtrable: boolean; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.filtrable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

function formatMoney(value: string | number | undefined | null): string {
  if (value === null || value === undefined) return '—'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(n)) return '—'
  return `$${new Intl.NumberFormat('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}`
}

function formatCurrency(value: number | undefined | null, moneda: string | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  try {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: moneda || 'USD',
    }).format(value)
  } catch {
    // Código de moneda no reconocido: fallback a monto + código.
    return `${formatMoney(value)} ${moneda ?? ''}`.trim()
  }
}

function formatFechaHora(iso: string | undefined | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('es-EC', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function CanjesFidelizacionPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)

  // "Todo el histórico": al activarse se omite el rango de fechas y todas las
  // métricas se calculan sobre el total de datos del programa. `modo` alterna
  // entre selector de mes y rango de fechas personalizado.
  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte()

  // Filtros globales del reporte aplicados a cards, charts y tabla.
  const [filtros, setFiltros] = useState<DynamicFiltersValue>({})
  // Filtro de "Estado" exclusivo de la tabla de canjes. No se aplica a KPIs ni
  // charts (filtrar los agregados por estado dejaría incoherentes las cards de
  // "Pendiente"/"Estado top"). Valores crudos del upstream: completado / en_revision.
  const [estadoCanje, setEstadoCanje] = useState<string | undefined>(undefined)

  // Datos base para los filtros cascada (Jefe de Ventas / Representante Médico):
  // pueblan las opciones de los dropdowns y acotan la lista de Representante.
  const participantes = useTodosParticipantesFidelizacion(configId)
  const camposExternos = metadata.data?.campos_externos
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const { cascadeSelects, inyectarReps, narrowRepOptions } = useCascadaRepresentante({
    configId,
    participantes: participantes.data,
    repKey: campoRepFiltro?.key,
    filtros,
    setFiltros,
  })
  // Filtros con el conjunto de representantes del jefe inyectado (coma-separado)
  // para el upstream; sin cascada activa es idéntico a `filtros`.
  const filtrosInyectados = useMemo(
    () => inyectarReps(filtros),
    [filtros, inyectarReps],
  )
  const filtrosCompletos = useMemo(
    () => ({ ...rangoEfectivo, ...filtrosInyectados }),
    [rangoEfectivo, filtrosInyectados],
  )
  // Filtros que recibe la tabla: los globales + el estado (solo tabla).
  const filtrosTabla = useMemo(
    () => (estadoCanje ? { ...filtrosInyectados, estado: estadoCanje } : filtrosInyectados),
    [filtrosInyectados, estadoCanje],
  )

  const resumen = useResumenFidelizacion(configId, filtrosCompletos)
  // Saldo de puntos: snapshot total (todos los usuarios, todo el histórico),
  // SIN filtro de fecha. Respeta filtros dinámicos (cadena/provincia) si hay.
  // Un saldo no debe depender del mes seleccionado.
  const resumenSaldo = useResumenFidelizacion(configId, filtrosInyectados)
  // Saldo de la wallet del tenant: global, sin filtros ni rango de fechas.
  const wallet = useWalletBalance(configId)
  const canjes = useMetricasCanjes(configId, filtrosCompletos)
  // Cantidad de canjes por día (línea gris secundaria).
  const serie = useSerieTemporal(configId, {
    granularidad: 'dia',
    metrica: 'canjes',
    ...filtrosCompletos,
  })
  // $ canjeado por día (línea de color principal, con etiquetas).
  const serieMonto = useSerieTemporal(configId, {
    granularidad: 'dia',
    metrica: 'canjes_monto',
    ...filtrosCompletos,
  })

  // Agrupaciones para "Canjes por agrupación" (Representante / Provincia / Distrito).
  // El endpoint `por-{campo}` / `por-provincia` devuelve la cantidad de canjes
  // por grupo en el campo `canjes`.
  const campoRepresentante = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const campoDistrito = useMemo(
    () => pickCampo(camposExternos, CAMPO_DISTRITO_CANDIDATES),
    [camposExternos],
  )
  const porProvinciaAgr = useMetricasPorProvincia(configId, filtrosCompletos)
  const porRepAgr = useMetricasPorCampo(configId, campoRepresentante?.key ?? null, {
    ...filtrosCompletos,
    top: 10,
  })
  const porDistritoAgr = useMetricasPorCampo(configId, campoDistrito?.key ?? null, {
    ...filtrosCompletos,
    top: 10,
  })
  // Lookup de participantes para resolver participante_id → nombre en la tabla.
  // El upstream de canjes no incluye el nombre, solo el id. Cargamos TODOS los
  // participantes (paginando internamente) para cubrir cualquier id, y reutilizamos
  // los mismos datos para armar los dropdowns de filtros con pares ID/nombre.
  const participantesById = useMemo(() => {
    const map = new Map<number | string, string>()
    for (const p of participantes.data ?? []) {
      const id = (p.id as number | string | undefined) ?? null
      if (id === null) continue
      const nombre = [p.nombre, p.apellido].filter(Boolean).join(' ').trim()
      if (nombre) map.set(id, nombre)
    }
    return map
  }, [participantes.data])


  // Campos filtrables para los dropdowns de la tabla de canjes (mismos que en
  // Registros/Ventas). El upstream filtra por ID, así que enviamos el ID como
  // value y mostramos el nombre como label.
  const campoCadenaFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_CADENA_CANDIDATES),
    [camposExternos],
  )

  const fieldOptionsFiltros = useMemo(() => {
    const out: Record<string, FieldOption[]> = {}
    const datos = participantes.data ?? []

    const collectPairs = (
      getId: (p: Record<string, unknown>) => unknown,
      getNombre: (p: Record<string, unknown>) => unknown,
      dedupePorNombre = false,
    ): FieldOption[] => {
      // El upstream a veces devuelve varios IDs y/o variantes de formato
      // (espacios sobrantes, mayúsculas, acentos) para una misma cadena, lo que
      // generaba opciones duplicadas en el filtro. Estandarizamos el nombre;
      // con `dedupePorNombre` (sólo cadena) deduplicamos por nombre normalizado
      // y conservamos un ID representativo estable (el menor). En representante
      // NO se dedupe por nombre: dos personas distintas pueden llamarse igual.
      const limpiar = (s: string) => s.trim().replace(/\s+/g, ' ')
      const clave = (s: string) =>
        limpiar(s).toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const esMenor = (a: string, b: string) => {
        const na = Number(a)
        const nb = Number(b)
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na < nb
        return a.localeCompare(b) < 0
      }

      const seen = new Map<string, { value: string; label: string }>()
      for (const p of datos) {
        const id = getId(p)
        if (id === undefined || id === null || String(id).trim() === '') continue
        const value = String(id).trim()
        const nombre = getNombre(p)
        const label = nombre ? limpiar(String(nombre)) : value
        const key = dedupePorNombre ? clave(label) : value
        const prev = seen.get(key)
        if (!prev) seen.set(key, { value, label })
        else if (dedupePorNombre && esMenor(value, prev.value)) {
          seen.set(key, { value, label: prev.label })
        }
      }
      return Array.from(seen.values()).sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
      )
    }

    const provinciaPairs = collectPairs(
      (p) => p.provincia,
      (p) => p.provincia_nombre,
    )
    if (provinciaPairs.length > 0) out.provincia = provinciaPairs

    if (campoCadenaFiltro?.key) {
      const k = campoCadenaFiltro.key
      out[k] = collectPairs(
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
        true,
      )
    }
    if (campoRepFiltro?.key) {
      const k = campoRepFiltro.key
      out[k] = collectPairs(
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
      )
    }

    // Resto de campos de texto filtrables (ej. Razón social): dropdown con los
    // valores reales del listado en vez de un input de texto libre.
    const cubiertos = [
      'provincia',
      ...(campoCadenaFiltro?.key ? [campoCadenaFiltro.key] : []),
      ...(campoRepFiltro?.key ? [campoRepFiltro.key] : []),
    ]
    for (const [k, opts] of Object.entries(
      autoFieldOptions(datos, camposExternos, cubiertos),
    )) {
      if (!out[k]) out[k] = opts
    }

    // Dependiente (reporte 7): dropdown de participantes registrados por nombre.
    if (configId === 7) {
      const pares = participantePairs(datos)
      if (pares.length > 0) out.participante_id = pares
    }
    return out
  }, [participantes.data, campoCadenaFiltro?.key, campoRepFiltro?.key, camposExternos, configId])

  const color = normalizeColor(config.data)
  const errorGlobal = config.error || metadata.error || resumen.error

  const handleRefresh = () => {
    metadata.refetch()
    resumen.refetch()
    resumenSaldo.refetch()
    wallet.refetch()
    canjes.refetch()
    serie.refetch()
    serieMonto.refetch()
    porProvinciaAgr.refetch()
    porRepAgr.refetch()
    porDistritoAgr.refetch()
  }

  const refreshing =
    metadata.isFetching ||
    resumen.isFetching ||
    wallet.isFetching ||
    canjes.isFetching ||
    serie.isFetching ||
    serieMonto.isFetching ||
    porProvinciaAgr.isFetching ||
    porRepAgr.isFetching ||
    porDistritoAgr.isFetching

  // El upstream entrega `por_estado` (no `por_producto`). Usamos eso para el chart.
  const canjesPorEstado = useMemo(() => {
    const entries = Object.entries(canjes.data?.por_estado ?? {})
    return entries.map(([estado, info]) => ({ label: estado, value: info.cantidad }))
  }, [canjes.data])

  const estadoTop = useMemo(() => {
    const sorted = [...canjesPorEstado].sort((a, b) => Number(b.value) - Number(a.value))
    return sorted[0]
  }, [canjesPorEstado])

  // total = $ canjeado (línea de color), secondary = cantidad de canjes (gris).
  const fechaData = useMemo(() => {
    const montoMap = new Map(
      (serieMonto.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor)]),
    )
    const cantMap = new Map(
      (serie.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor)]),
    )
    const fechas = Array.from(new Set([...montoMap.keys(), ...cantMap.keys()])).sort()
    return fechas.map((f) => ({
      fecha: f,
      total: montoMap.get(f) ?? 0,
      secondary: cantMap.get(f) ?? 0,
    }))
  }, [serieMonto.data, serie.data])

  // Datasets de canjes por agrupación: usamos el campo `canjes` (count) de la
  // respuesta de agrupación y descartamos los grupos sin canjes para que el
  // chart sea legible.
  const repCanjesData = useMemo(
    () =>
      (porRepAgr.data?.datos ?? [])
        .map((d) => ({ label: d.valor, value: Number(d.monto_canjeado ?? 0) }))
        .filter((d) => d.value > 0),
    [porRepAgr.data],
  )
  const provinciaCanjesData = useMemo(
    () =>
      (porProvinciaAgr.data?.datos ?? [])
        .map((d) => ({ label: d.valor, value: Number(d.monto_canjeado ?? 0) }))
        .filter((d) => d.value > 0),
    [porProvinciaAgr.data],
  )
  // El upstream de Medifarma Perú devuelve el ID del distrito en `valor` (no el
  // nombre). Lo resolvemos con el catálogo local `ciudad` id → nombre.
  const distritoCanjesData = useMemo(
    () =>
      (porDistritoAgr.data?.datos ?? [])
        .map((d) => ({
          label: nombreDistritoReporte(configId, d.valor) ?? d.valor,
          value: Number(d.monto_canjeado ?? 0),
        }))
        .filter((d) => d.value > 0),
    [porDistritoAgr.data, configId],
  )

const totalRepresentantes = (porRepAgr.data?.datos ?? []).filter(d => d.valor !== '(sin asignar)' && (d.canjes ?? 0) > 0).length
  const totalProvincias = (porProvinciaAgr.data?.datos ?? []).filter(d => d.valor !== '(sin asignar)' && (d.canjes ?? 0) > 0).length
  const totalDistritos = (porDistritoAgr.data?.datos ?? []).filter(d => d.valor !== '(sin asignar)' && (d.canjes ?? 0) > 0).length

  return (
    <div className="space-y-6">
      <ReporteHeader
        config={config.data}
        programaNombre={metadata.data?.programa.nombre}
        icon={Gift}
        seccion="Canjes"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <ReporteSubNav
        configId={configId}
        colorNombre={color.nombre}
        colorTono={color.tono}
      />

      <ReportePeriodoBar
        rango={rango}
        mes={mes}
        onMesChange={setMes}
        minDate={metadata.data?.rango_datos?.primera_factura ?? null}
        maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
        configId={configId}
        colorNombre={color.nombre}
        exportTipos={[
          { tipo: 'canjes', label: 'Canjes' },
          { tipo: 'productos', label: 'Catálogo de productos' },
        ]}
        exportFiltros={filtrosInyectados}
        sinFecha={sinFecha}
        onSinFechaChange={setSinFecha}
        modo={modo}
        onModoChange={setModo}
        rangoPersonalizado={rangoPersonalizado}
        onRangoPersonalizadoChange={setRangoPersonalizado}
      />

      {/* Filtros globales del reporte: provincia / cadena / representante.
          Aplican a cards, charts y tabla. */}
      <DynamicFiltersBar
        metadata={metadata.data}
        value={filtros}
        onChange={setFiltros}
        hideDateRange
        omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
        fieldOptions={narrowRepOptions(fieldOptionsFiltros)}
        cascadeSelects={cascadeSelects}
        extraSelects={[
          {
            key: 'estado',
            label: 'Estado',
            value: estadoCanje ?? '__all__',
            onChange: (v) => setEstadoCanje(v === '__all__' ? undefined : v),
            options: [
              { value: '__all__', label: 'Todos' },
              { value: 'completado', label: 'Completado' },
              { value: 'en_revision', label: 'En revisión' },
            ],
          },
        ]}
      />

      {errorGlobal && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(errorGlobal).message}</AlertDescription>
        </Alert>
      )}

      {/* KPIs consolidados */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCardConsolidado
          icon={Gift}
          title="Canjes del mes"
          value={resumen.data?.canjes.total ?? 0}
          subStats={[
            { label: 'Monto canjeado', value: formatMoneda(resumen.data?.canjes.monto_total, configId), tone: 'success' },
            {
              label: 'Pendiente',
              value: formatMoneda(canjes.data?.por_estado?.en_revision?.monto, configId),
              tone: 'warning',
            },
            {
              label: 'Estado top',
              value: estadoTop ? estadoTop.label : '—',
              tone: 'muted',
            },
          ]}
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={resumen.isLoading || canjes.isLoading}
        />
        <StatCardConsolidado
          icon={Coins}
          title="Puntos disponibles"
          hint="Totales históricos · todos los usuarios"
          value={parseNumeric(resumenSaldo.data?.puntos.disponibles)}
          subStats={[
            { label: `Canjeados (${monedaPais(configId).simbolo})`, value: formatMoneda(resumenSaldo.data?.canjes.monto_total, configId), tone: 'warning' },
            {
              label: 'Canjeados (pts)',
              value: (() => {
                const monto = parseNumeric(resumenSaldo.data?.canjes.monto_total)
                const tasa = metadata.data?.valor_punto ?? 0
                return tasa > 0 ? Math.round(monto / tasa) : '-'
              })(),
              tone: 'muted',
            },
          ]}
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={resumenSaldo.isLoading}
        />
        <StatCardConsolidado
          icon={Wallet}
          title="Saldo wallet"
          hint={
            wallet.data
              ? `Actualizado: ${formatFechaHora(wallet.data.consultado_en)}`
              : undefined
          }
          value={
            wallet.error
              ? '—'
              : esPeru(configId)
                ? formatMoneda(wallet.data?.balance, configId)
                : formatCurrency(wallet.data?.balance, wallet.data?.moneda)
          }
          subStats={
            wallet.data
              ? [
                  {
                    label: 'Moneda',
                    value: esPeru(configId) ? monedaPais(configId).simbolo : wallet.data.moneda,
                    tone: 'muted',
                  },
                ]
              : undefined
          }
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={wallet.isLoading}
        />
      </div>

      {/* Canjes por agrupación (izquierda) + Canjes día a día (derecha).
          Ambas estiran a la misma altura (la de barras define el alto, ya
          capada por maxVisible) y la de línea la llena con `fill`. */}
      <div className="grid gap-4 md:grid-cols-2">
        <BarChartAgrupado
          title="Canjes por agrupación"
          colorNombre={color.nombre}
          icon={Layers}
          formatValue={(v) => formatMoneda(v, configId)}
          options={(
            [
              {
                key: 'representante',
                label: campoRepresentante?.label ?? 'Representante',
                icon: UserCheck,
                data: repCanjesData,
                isLoading: porRepAgr.isLoading,
                error: porRepAgr.error,
                headerStat: { value: totalRepresentantes, label: 'únicos' },
              },
              {
                key: 'provincia',
                label: regionTerminoCap(configId),
                icon: MapIcon,
                data: provinciaCanjesData,
                isLoading: porProvinciaAgr.isLoading,
                error: porProvinciaAgr.error,
                headerStat: { value: totalProvincias, label: 'únicas' },
              },
              {
                key: 'distrito',
                label: campoDistrito?.label ?? 'Distrito',
                icon: MapPin,
                data: distritoCanjesData,
                isLoading: porDistritoAgr.isLoading,
                error: porDistritoAgr.error,
                headerStat: { value: totalDistritos, label: 'únicos' },
              },
            ] satisfies DatasetOpcion[]
          )}
          configId={configId}
        />
        <AreaChartFecha
          title="Canjes día a día"
          data={fechaData}
          colorNombre={color.nombre}
          colorTono={color.tono}
          isLoading={serie.isLoading || serieMonto.isLoading}
          icon={TrendingUp}
          fill
          formatValue={(v) => formatMoneda(v, configId)}
          primaryLabel="Canjeado ($)"
          secondaryLabel="Cantidad de canjes"
          secondaryFormatValue={(v) => formatNumero(Math.round(v), configId)}
          configId={configId}
        />
      </div>

      {/* Tabla canjes: el upstream entrega `participante_id` pero no el nombre.
          Resolvemos a partir del listado de participantes.
          La barra de filtros se renderiza arriba a nivel de página. */}
      <ListadoTab
        configId={configId}
        recurso="canjes"
        metadata={metadata.data}
        rango={rangoEfectivo}
        hideDateRange
        omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
        fieldOptions={fieldOptionsFiltros}
        filtros={filtrosTabla}
        onFiltrosChange={setFiltros}
        hideFiltersBar
        enrichRow={(row) => {
          const id = row.participante_id as number | string | undefined
          if (id === undefined) return {}
          const nombre = participantesById.get(id)
          return nombre ? { participante_nombre: nombre } : {}
        }}
      />

      {/* Indicador visual referencial */}
      {!resumen.isLoading && (resumen.data?.canjes.total ?? 0) === 0 && (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm gap-2">
          <Trophy className="h-4 w-4" />
          Sin canjes en este mes
        </div>
      )}
    </div>
  )
}
